import { Check, ChevronDown, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type SearchableOption = {
  value: string;
  label: string;
  keywords?: string;
};

type SearchableSelectProps = {
  id?: string;
  name?: string;
  ariaLabel?: string;
  value: string;
  options: SearchableOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
};

export function SearchableSelect({
  id,
  name,
  ariaLabel,
  value,
  options,
  onChange,
  placeholder = "--",
  searchPlaceholder = "Search...",
  emptyText = "No matches",
  disabled = false,
}: SearchableSelectProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const selected = options.find((option) => option.value === value);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [menuPosition, setMenuPosition] = useState<{
    left: number;
    top?: number;
    bottom?: number;
    width: number;
    maxHeight: number;
  }>();
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const filtered = options.filter((option) => {
    if (!normalizedQuery) return true;
    return `${option.label} ${option.keywords ?? ""}`.toLocaleLowerCase().includes(normalizedQuery);
  });
  const effectiveHighlightedIndex = filtered.length
    ? Math.min(highlightedIndex, filtered.length - 1)
    : 0;

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  useEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const gap = 4;
      const viewportGap = 8;
      const preferredHeight = 260;
      const spaceBelow = window.innerHeight - rect.bottom - viewportGap;
      const spaceAbove = rect.top - viewportGap;
      const openAbove = spaceBelow < 160 && spaceAbove > spaceBelow;
      setMenuPosition({
        left: Math.max(
          viewportGap,
          Math.min(rect.left, window.innerWidth - rect.width - viewportGap),
        ),
        top: openAbove ? undefined : rect.bottom + gap,
        bottom: openAbove ? window.innerHeight - rect.top + gap : undefined,
        width: rect.width,
        maxHeight: Math.max(120, Math.min(preferredHeight, openAbove ? spaceAbove : spaceBelow)),
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !filtered[effectiveHighlightedIndex]) return;
    document
      .getElementById(`${listId}-${effectiveHighlightedIndex}`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [effectiveHighlightedIndex, filtered, listId, open]);

  const openMenu = () => {
    if (disabled) return;
    setOpen(true);
    setQuery("");
    setHighlightedIndex(0);
  };

  const choose = (option: SearchableOption) => {
    onChange(option.value);
    setQuery(option.label);
    setOpen(false);
    setHighlightedIndex(0);
  };

  const clear = () => {
    onChange("");
    setQuery("");
    setOpen(true);
    setHighlightedIndex(0);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) {
        setQuery("");
        setOpen(true);
        setHighlightedIndex(0);
        return;
      }
      setOpen(true);
      setHighlightedIndex((current) =>
        filtered.length ? Math.min(current + 1, filtered.length - 1) : 0,
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setQuery("");
        setOpen(true);
        setHighlightedIndex(0);
        return;
      }
      setOpen(true);
      setHighlightedIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === "Enter" && open && filtered[effectiveHighlightedIndex]) {
      event.preventDefault();
      choose(filtered[effectiveHighlightedIndex]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      setQuery(selected?.label ?? "");
    } else if (event.key === "Tab") {
      setOpen(false);
      setQuery(selected?.label ?? "");
    }
  };

  return (
    <div className="searchable-select" ref={rootRef}>
      <div className={`searchable-control ${open ? "open" : ""}`}>
        <input
          ref={inputRef}
          id={id}
          name={name}
          role="combobox"
          aria-label={ariaLabel}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={
            open && filtered[effectiveHighlightedIndex]
              ? `${listId}-${effectiveHighlightedIndex}`
              : undefined
          }
          disabled={disabled}
          placeholder={open ? searchPlaceholder : placeholder}
          value={open ? query : (selected?.label ?? "")}
          onFocus={openMenu}
          onChange={(event) => {
            setOpen(true);
            setQuery(event.target.value);
            setHighlightedIndex(0);
          }}
          onKeyDown={handleKeyDown}
        />
        {value && !disabled && (
          <button
            type="button"
            className="searchable-clear"
            aria-label="Clear selection"
            title="Clear selection"
            onMouseDown={(event) => event.preventDefault()}
            onClick={clear}
          >
            <X size={14} />
          </button>
        )}
        <ChevronDown className="searchable-chevron" size={16} aria-hidden="true" />
      </div>
      {open && !disabled && menuPosition
        ? createPortal(
            <div
              ref={menuRef}
              className="searchable-menu"
              id={listId}
              role="listbox"
              style={menuPosition}
            >
              {filtered.length ? (
                filtered.map((option, index) => (
                  <div
                    id={`${listId}-${index}`}
                    key={option.value}
                    role="option"
                    aria-selected={option.value === value}
                    className={`searchable-option ${index === effectiveHighlightedIndex ? "highlighted" : ""}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => choose(option)}
                  >
                    <span>{option.label}</span>
                    {option.value === value && <Check size={15} aria-hidden="true" />}
                  </div>
                ))
              ) : (
                <div className="searchable-empty">{emptyText}</div>
              )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
